/**
 * Weekly digest — a plain-text summary of key metrics and focus areas,
 * composed from AQI, follow-up reminders, and system scan findings.
 *
 * Used by the dashboard "Email weekly digest" button (queues to outbox
 * collection) and by the Slack notify seam (fire-and-forget post).
 */

import type {
  Account,
  DeployedAgent,
  Person,
  ProjectTask,
  Ticket,
} from '../db/types';
import { computeAqi, aqiBand } from './aqi';
import { getFollowUpReminders } from './reminders';
import { scanSystem } from '../agent/systemScanner';
import { postToSlack } from '../agent/slackNotify';
import { db } from '../db/store';

export interface DigestInput {
  people: Person[];
  accounts: Account[];
  tickets: Ticket[];
  tasks: ProjectTask[];
  agents: DeployedAgent[];
}

export interface DigestOutput {
  /** Email subject line. */
  subject: string;
  /** Plain-text body. */
  text: string;
}

/**
 * Compose a weekly digest: confident, plain prose covering AQI, reminders,
 * and top findings from the system scan.
 */
export function composeWeeklyDigest(input: DigestInput): DigestOutput {
  const aqi = computeAqi(input);
  const reminders = getFollowUpReminders(input.people);
  const scan = scanSystem({
    tickets: input.tickets,
    tasks: input.tasks,
    people: input.people,
    accounts: input.accounts,
  });

  const weekStart = new Date();
  weekStart.setDate(weekStart.getDate() - weekStart.getDay());
  const weekEnd = new Date(weekStart);
  weekEnd.setDate(weekEnd.getDate() + 6);

  const fmtDate = (d: Date): string =>
    d.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });

  const dateRange = `${fmtDate(weekStart)}–${fmtDate(weekEnd)}`;

  // --- Build narrative sections ---

  const sections: string[] = [];

  // 1. AQI and band
  const band = aqiBand(aqi.score);
  const weakest = [...aqi.components].sort((a, b) => a.score - b.score)[0];
  sections.push(
    `AQI Score: ${aqi.score}/100 (${band}). ` +
      `The biggest drag is ${weakest.label.toLowerCase()}: ${weakest.detail.toLowerCase()}.`,
  );

  // 2. Follow-up reminders
  const overdue = reminders.filter((r) => r.urgency === 'overdue');
  const today = reminders.filter((r) => r.urgency === 'today');
  const soon = reminders.filter((r) => r.urgency === 'soon');

  if (reminders.length === 0) {
    sections.push('Follow-ups: None due or overdue — you\'re on top of the pipeline.');
  } else {
    const parts: string[] = [];
    if (overdue.length) parts.push(`${overdue.length} overdue`);
    if (today.length) parts.push(`${today.length} due today`);
    if (soon.length) parts.push(`${soon.length} due soon`);
    sections.push(`Follow-ups: ${parts.join(', ')}.`);
  }

  // 3. Top findings (high severity only, limit to 5)
  const topFindings = scan.findings
    .filter((f) => f.severity === 'high')
    .slice(0, 5);

  if (topFindings.length === 0) {
    sections.push('System scan: All clear — no high-severity findings.');
  } else {
    const titles = topFindings.map((f) => f.title).join('; ');
    sections.push(
      `High-severity findings (${topFindings.length}): ${titles}.`,
    );
  }

  // 4. Open tickets
  const openTickets = input.tickets.filter(
    (t) => t.status !== 'done' && t.status !== 'cancelled',
  );
  sections.push(`Open tickets: ${openTickets.length}.`);

  // 5. Tasks due this week
  const now = Date.now();
  const weekOut = now + 7 * 24 * 60 * 60 * 1000;
  const dueSoon = input.tasks.filter(
    (t) => t.dueDate && Date.parse(t.dueDate) <= weekOut && t.status !== 'done',
  );
  sections.push(`Tasks due this week: ${dueSoon.length}.`);

  const text = sections.join('\n\n');
  const subject = `Argo Weekly Digest — ${dateRange} (AQI ${aqi.score})`;

  return { subject, text };
}

/**
 * Queue a weekly digest to the outbox collection. The sendEmail Cloud
 * Function will pick it up and send via Brevo. In local dev / simulation,
 * writes succeed but nothing is actually sent. Also fire-and-forget posts
 * to Slack with the digest headline and first lines.
 */
export async function queueWeeklyDigest(
  to: string,
  input: DigestInput,
): Promise<void> {
  const digest = composeWeeklyDigest(input);
  const now = new Date().toISOString();

  await db.add('outbox', {
    id: crypto.randomUUID?.() || `digest-${Date.now()}`,
    to,
    subject: digest.subject,
    textContent: digest.text,
    status: 'queued',
    createdAt: now,
    updatedAt: now,
  });

  // Fire-and-forget Slack notification with digest subject + first few lines.
  const slackText = `${digest.subject}\n\n${digest.text.split('\n\n').slice(0, 2).join('\n\n')}`;
  postToSlack(slackText).catch((err) => {
    // eslint-disable-next-line no-console
    console.error('[digest] Slack post failed:', err);
  });
}
